import { useCallback, useEffect, useState } from 'react';
import InputSelect from '../../../../../component/inputSelect/InputSelect';
import tooltipIcon from '../../../../../assets/image/tooltip_vendor name.png';
import InputField from '../../../../../component/form/FormInput';
import Button from '../../../../../component/ui/Button';
import Modal from '../../../../../component/modal/Modal';
import { getCurrentDate, getDate } from '../../../../../utils/dateUtils';
import usePaymentMethod from '../../../../purchasebills/hooks/usePaymentMethod';
import AddPaymentMethodModal from '../../../../purchasebills/components/AddPaymentMethodModal';
import { getCustomerDetail, getCustomerList } from '../../../../../services/customers';
import { getDoctorList } from '../../../../../services/doctors';
import { useLocation } from 'react-router-dom';
import { fetchInvoiceDetails, fetchInvoiceNumber } from '../../../../../services/invoices';
import { addDays, format, parseISO } from 'date-fns';
import AddCustomerModel from '../components/addCustomerModel';
import AddDoctorModel from '../components/addDoctorModel';
import CustomDrawer from '../../../components/customDrawer';
import InvoiceTableHeader from './invoiceProductTable/invoiceTableHeader';
import InvoiceFooter from './InvoiceFooter';
import { mergeUniqueById } from '../../../../../utils/controller';
import { getSalesAccountsDropdownList } from '../../../../../services/settings/accounting';
import CreateAccountsModal from '../../../settings/accounting/components/createAccountsModal';
import SaleAccountAlertWarningModal from '../components/saleAccountAlertWarningModal';
import infoIcon from '../../../../../assets/image/info.svg';

const InvoiceForm = ({
	showRestoreBanner,
	id,
	error,
	setError,
	invoice,
	setInvoice,
	setOldInvoice,
	setInvoiceTable,
	setOldInvoiceTable,
	setAdditionDiscountAndTax,
	tableError,
	setTableError,
	isDraft,
	setIsDraft,
	invoiceTable,
	additionDiscountAndTax,
	totalDiscountAmount,
	totalTaxesAmount,
	totalAmount,
	calculateAmount,
	setOriginalData,
	onInvoiceLoaded,
}) => {
	const [saleOrders, setSaleOrders] = useState([]);
	const [customerList, setCustomerList] = useState([]);
	const [doctorList, setDoctorList] = useState([]);
	const [accountList, setAccountList] = useState([]);
	const { paymentMethodList, setSearchPaymentMethod } = usePaymentMethod();
	const [isDateFocus, setIsDateFocus] = useState(false);
	const [isRemarkFocus, setIsRemarkFocus] = useState(false);
	const [isAddPaymentMethod, setIsAddPaymentMethod] = useState(false);
	const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
	const [isAddDoctorModalOpen, setIsAddDoctorModalOpen] = useState(false);
	const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
	const [isPaymentMethodFocus, setIsPaymentMethodFocus] = useState(false);
	const location = useLocation();
	const [drawerType, setDrawerType] = useState('');
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [searchCustomer, setSearchCustomer] = useState(null);
	const [searchDoctor, setSearchDoctor] = useState(null);
	const [searchAccount, setSearchAccount] = useState(null);
	const [isOpenAccountWarningModal, setIsOpenAccountWarningModal] = useState(false);
	const [isOpenPastDate, setIsOpenPastDate] = useState(false);
	const [isUsedPastDate, setIsUsedPastDate] = useState(false);
	const [autofocus, setAutofocus] = useState(false);
	const [productsList, setProductsList] = useState([]);

	const [params, setParams] = useState({
		customer: {
			status: 1,
			page: 1,
			pageSize: 10000,
			dropdown: true,
			orderBy: null,
			sortBy: null,
			filterBy: null,
			filterValue: null,
		},
		doctor: {
			status: 1,
			page: 1,
			pageSize: 10000,
			dropdown: true,
			orderBy: null,
			sortBy: null,
			filterBy: null,
			filterValue: null,
		},
		account: {
			status: 1,
			type: 'sales',
			page: 1,
			pageSize: 10000,
			search: null,
		},
	});

	const handleFetchCustomerList = useCallback(async () => {
		try {
			const { customers } = await getCustomerList(params?.customer);
			if (customers) {
				const customerOptionList = customers?.map((customer) => {
					return {
						key: customer?.id,
						value: customer?.name,
					};
				});
				if (customerList.length === 1 && id) {
					const list = mergeUniqueById(customerOptionList, doctorList);
					setCustomerList(list);
				} else {
					setCustomerList(customerOptionList);
				}
			}
		} catch (error) { }
	}, [params?.customer]);

	const handleFetchAccountList = useCallback(async () => {
		try {
			const data = await getSalesAccountsDropdownList(params?.account);
			if (data) {
				const accountOptionList = data.flatMap((item) =>
					item.category_list.map((account) => ({
						key: account.account_id,
						value: account.account_name,
						category: item.category_name,
					})),
				);

				if (accountList.length === 1 && id) {
					const list = mergeUniqueById(accountOptionList, doctorList);
					setAccountList(list);
				} else {
					if (searchAccount === null && !id && invoice.account_id === null) {
						const accountData = accountOptionList.find((data) => data.value === 'Sales');
						if (accountData) {
							setInvoice((prev) => ({
								...prev,
								account_id: accountData.key,
							}));
						}
					}
					setAccountList(accountOptionList);
				}
			}
		} catch (error) { }
	}, [params?.account]);

	const handleFetchDoctorList = useCallback(async () => {
		try {
			const { doctors } = await getDoctorList(params?.doctor);
			if (doctors) {
				const doctorOptionList = doctors?.map((doctor) => {
					return {
						key: doctor?.id,
						value: doctor?.name,
					};
				});
				if (doctorList.length === 1 && id) {
					const list = mergeUniqueById(doctorOptionList, doctorList);
					setDoctorList(list);
				} else {
					setDoctorList(doctorOptionList);
				}
			}
		} catch (error) { }
	}, [params?.doctor]);

	async function updateDueDateByCustomer() {
		if (invoice.customer_id && invoice.payment_method) {
			const method = paymentMethodList.find((item) => item.key === invoice.payment_method);
			if (
				invoice.customer_id &&
				method !== undefined &&
				method.value.toLowerCase().includes('credit')
			) {
				try {
					const { customer_details } = await getCustomerDetail(invoice.customer_id);
					const invoiceDate = invoice.date ? parseISO(invoice.date) : new Date();
					const ceditDays =
						customer_details?.credit_days !== null ? Number(customer_details?.credit_days) : 0;
					const dateString = addDays(invoiceDate, ceditDays);
					const dueDate = format(dateString, 'yyyy-MM-dd');
					setInvoice((prev) => ({
						...prev,
						due_date: dueDate,
					}));
				} catch (error) { }
			}
		}
	}

	const confirmUsedPastDate = () => {
		const newError = { ...error };
		delete newError.date;
		setError(newError);
		setIsUsedPastDate(true);
		setIsOpenPastDate(false);
		setIsRemarkFocus(true);
	};

	const cancelUsedPastDate = () => {
		setInvoice((prev) => ({
			...prev,
			date: getCurrentDate(),
		}));
		setIsOpenPastDate(false);
		setIsDateFocus(true);
	};

	const handleFormData = (type, value) => {
		setInvoice((prev) => ({
			...prev,
			[type]: value,
		}));

		let newError = { ...error };
		if (type === 'customer_id') {
			if (value === '' || value === null || value === undefined) {
				newError[type] = 'Please select valid  customer name from the list';
			} else {
				delete newError[type];
			}
		}

		if (type === 'payment_method') {
			if (value === '' || value === null || value === undefined) {
				newError[type] = 'Please select valid  payment method from the list';
			} else {
				delete newError[type];
			}
		}
		setError(newError);
	};

	useEffect(() => {
		let timer = setTimeout(() => {
			setParams((prev) => ({
				...prev,
				account: {
					...prev.account,
					search: searchAccount,
					status: 1,
				},
			}));
		}, 500);
		return () => clearTimeout(timer);
	}, [searchAccount]);

	useEffect(() => {
		let timer = setTimeout(() => {
			setParams((prev) => ({
				...prev,
				doctor: {
					...prev.doctor,
					filterBy: 'name',
					filterValue: searchDoctor,
					status: 1,
				},
			}));
		}, 500);
		return () => clearTimeout(timer);
	}, [searchDoctor]);

	useEffect(() => {
		let timer = setTimeout(() => {
			setParams((prev) => ({
				...prev,
				customer: {
					...prev.customer,
					filterBy: 'name',
					filterValue: searchCustomer,
					status: 1,
				},
			}));
		}, 500);
		return () => clearTimeout(timer);
	}, [searchCustomer]);

	useEffect(() => {
		if (!invoice.account_id || invoice.account_id === 17) {
			handleFetchAccountList();
		}
	}, [params.account, id, invoice.account_id]);

	useEffect(() => {
		if (!invoice.customer_id) {
			handleFetchCustomerList();
		}
	}, [params.customer, id, invoice.customer_id]);

	useEffect(() => {
		if (!invoice.doctor_id) {
			handleFetchDoctorList();
		}
	}, [params.doctor, id, invoice.doctor_id]);

	useEffect(() => {
		updateDueDateByCustomer();
	}, [invoice.customer_id, invoice.payment_method, invoice.date]);

	async function getInvoiceDetails(invoiceId) {
		try {
			var body = {
				ids: [invoiceId],
			};
			var { invoice } = await fetchInvoiceDetails(body);
			if (invoice) {
				const {
					invoice_number,
					date,
					due_date,
					remarks,
					payment_method_id,
					customer,
					doctor,
					account,
					is_draft,
					sale_order_id,
					doctor_id,
					account_id,
					customer_id,
					invoice_products,
					adjustment,
					discount,
					round_off,
					sub_total_amount,
					payment_method,
				} = invoice;

				const InvoiceProductList = generateInvoiceProducts(invoice_products);
				setOldInvoiceTable(InvoiceProductList);
				const discountAndTax = {
					discount:
						invoice_products.length > 0
							? Number((Number(discount ?? 0) * 100) / Number(sub_total_amount ?? 0)).toFixed(2)
							: 0,
					adj: Number(adjustment ?? 0),
					round_off: Number(0),
				};

				const apiInvoiceData = {
					customer_id,
					doctor_id,
					account_id,
					date: date !== null ? format(parseISO(date), 'yyyy-MM-dd') : null,
					due_date: due_date !== null ? format(parseISO(due_date), 'yyyy-MM-dd') : null,
					sale_order_id,
					remarks,
					payment_method: payment_method_id,
					invoice_number,
				};

				setOriginalData(apiInvoiceData, InvoiceProductList, discountAndTax);

				if (payment_method) {
					paymentMethodList.push({
						key: payment_method.payment_method_id,
						value: payment_method.payment_method_name,
					});
				}

				setOldInvoice((prev) => ({
					...prev,
					customer_id: customer_id,
					doctor_id: doctor_id,
					account_id: account_id || null,
					date: date !== null ? format(parseISO(date), 'yyyy-MM-dd') : null,
					due_date: due_date !== null ? format(parseISO(due_date), 'yyyy-MM-dd') : null,
					sale_order_id: sale_order_id,
					remarks: remarks,
					payment_method: payment_method_id,
				}));

				setIsDraft(is_draft);
				if (id && !id.includes('id')) {
					setInvoice((prev) => ({
						...prev,
						invoice_number: invoice_number,
					}));
				}
				// const list = invoice_products.map((item) => {
				// 	return {
				// 		key: item?.product_id,
				// 		value: item?.product?.product_name,
				// 	})),
				// );
				setSearchCustomer(customer?.name);
				setSearchDoctor(doctor?.name);
				setSearchAccount(account?.account_name || null);
				if (doctor) {
					setDoctorList((prev) => {
						const safePrev = Array.isArray(prev) ? prev : [];
						const exists = safePrev.some((item) => item.key === doctor.id);
						if (exists) {
							return prev;
						}
						return [...prev, { key: doctor.id, value: doctor.name }];
					});
				}
				setCustomerList((prev) => {
					const safePrev = Array.isArray(prev) ? prev : [];
					const exists = safePrev.some((item) => item.key === customer.id);
					if (exists) {
						return prev;
					}
					return [...prev, { key: customer.id, value: customer.name }];
				});

				setAdditionDiscountAndTax((prev) => ({
					...prev,
					discount:
						invoice_products.length > 0 &&
							sub_total_amount !== null &&
							Number(sub_total_amount) !== 0
							? Number((Number(discount ?? 0) * 100) / Number(sub_total_amount ?? 0)).toFixed(2)
							: 0,
					adj: Number(adjustment ?? 0),
					round_off: Number(0),
				}));
				setInvoice((prev) => ({ ...prev, ...apiInvoiceData }));
				if (id && !id.includes('id')) {
					setInvoice((prev) => ({ ...prev, invoice_number }));
				}
				setInvoiceTable(InvoiceProductList);
				setAdditionDiscountAndTax((prev) => ({ ...prev, ...discountAndTax }));
			}
		} catch (error) {
		} finally {
			onInvoiceLoaded?.(); // ✅ always unblock saves, even if fetch fails
		}
	}

	function generateInvoiceProducts(products) {
		const invoice_products = products.reduce((acc, product) => {
			acc.push({
				product_id: product?.product_id,
				batch_no: product?.product_batch?.batch_no || '',
				product_batch_id: product?.product_batch_id || null,
				quantity: product?.quantity || '',
				expiry_date:
					product?.expiry !== null && product?.expiry !== ''
						? getDate(product?.expiry, true, true, true)
						: '',
				mrp: product?.mrp,
				amount: product?.amount || '',
				tag:
					product.product_batch?.tags && product.product_batch?.tags.length > 0
						? product.product_batch?.tags.includes('expired')
							? 'expired'
							: product.product_batch?.tags.includes('near_expiry')
								? 'near_expiry'
								: product.product_batch?.tags[0]
						: '',
				product_tax1: Number(product?.product?.tax1),
				product_tax2: Number(product?.product?.tax2),
			});
			return acc;
		}, []);
		return invoice_products;
	}

	useEffect(() => {
		if (!id || id.includes('id')) {
			async function handleFetchInvoiceNumber() {
				try {
					const invoice = await fetchInvoiceNumber();
					handleFormData('invoice_number', invoice?.number);
					handleFormData('date', getCurrentDate());
				} catch (error) { }
			}
			handleFetchInvoiceNumber();
		}
		if (id) {
			if (id && !id.includes('id')) {
				getInvoiceDetails(id);
			} else if (id && id.includes('id')) {
				const params = new URLSearchParams(id);
				const map = Object.fromEntries(params.entries());
				getInvoiceDetails(map.id);
			}
		}
	}, [id]);

	function setCustomerData() {
		if (location && location?.state) {
			const state = location?.state;
			setCustomerList([state]);
			setInvoice((prev) => ({
				...prev,
				customer_id: state?.key,
			}));
		}
	}
	const setProductList = () => {
		if (location && location?.state && location.state?.products?.length > 0) {
			const newData = location.state?.products.map((item) => ({
				product_id: item?.id,
				batch_no: '',
				expiry_date: '',
				mrp: '0.00',
				quantity: '0',
				amount: '0.00',
				product_batch_id: null,
				product_tax1: item?.tax1,
				product_tax2: item?.tax2,
			}));
			setInvoiceTable(newData);
		}
	};

	useEffect(() => {
		if (location && location?.state && location.state?.products?.length > 0) {
			setProductList();
		} else if (location && location?.state) {
			setCustomerData();
		}
	}, [location]);

	useEffect(() => {
		checkInvoiceDueDateValidation();
	}, [invoice.date, invoice.due_date]);

	const checkInvoiceDueDateValidation = () => {
		const newError = { ...error };

		if (!invoice.date || !invoice.due_date) {
			return;
		}

		if (invoice.date) {
			if (new Date(invoice.due_date) < new Date(invoice.date)) {
				newError.due_date = 'Please select due date greater than invoice date';
			} else {
				delete newError.due_date;
			}
		} else {
			newError.due_date = 'Please select due date greater than invoice date';
		}
		setError(newError);
	};

	useEffect(() => {
		if (!autofocus) {
			setAutofocus(true);
		}
	}, [autofocus]);

	return (
		<div>
			<div className="max-w-1297 px-3 py-3">
				<div className="row">
					<div className="col-4">
						<InputSelect
							options={customerList}
							label="Customer Name"
							placeholder="Customer Name"
							autoComplete="off"
							required={true}
							returnInputValue={false}
							value={invoice?.customer_id}
							onSelect={(option) => {
								handleFormData('customer_id', option.key);
							}}
							onChange={(value) => {
								if (value === '' || value === null) {
									handleFormData('customer_id', value);
								}
								setSearchCustomer(value);
							}}
							error={error?.customer_id}
							autoFocus={autofocus}
							leftIcons={
								invoice?.customer_id
									? [
										{
											src: tooltipIcon,
											alt: 'Show Customer Details',
											onClick: () => {
												setDrawerType('customer');
												setIsDrawerOpen(true);
											},
										},
									]
									: []
							}
							onAddNew={() => {
								setIsAddCustomerModalOpen(true);
							}}
						/>
					</div>
					<div className="col-4">
						<InputSelect
							options={doctorList}
							label="Doctor Name"
							placeholder="Doctor Name"
							autoComplete="off"
							required={false}
							returnInputValue={false}
							value={invoice?.doctor_id}
							onChange={(value) => {
								if (value === '' || value === null) {
									handleFormData('doctor_id', value);
								}
								setSearchDoctor(value);
							}}
							onSelect={(option) => {
								handleFormData('doctor_id', option.key);
							}}
							error={error?.doctor_id}
							leftIcons={
								invoice?.doctor_id
									? [
										{
											src: tooltipIcon,
											alt: 'Show Doctor Details',
											onClick: () => {
												setDrawerType('doctor');
												setIsDrawerOpen(true);
											},
										},
									]
									: []
							}
							onAddNew={() => setIsAddDoctorModalOpen(true)}
						/>
					</div>
					<div className="col-2">
						<InputField
							label="Invoice #"
							placeholder="INV-001"
							autoComplete="off"
							type="text"
							required={true}
							disabled={true}
							error={error?.invoice_number}
							value={invoice.invoice_number}
							onChange={(value) => {
								handleFormData('invoice_number', value);
							}}
						/>
					</div>
					<div className="col-2">
						<InputField
							label="Date"
							placeholder="Enter Date"
							type="date"
							autoComplete="off"
							required={true}
							error={error?.date}
							autoFocus={isDateFocus}
							value={invoice.date}
							onBlur={(e) => {
								var value = e.target.value;
								const newError = { ...error };
								var date = new Date(value);
								var currentDate = new Date();
								currentDate.setDate(currentDate.getDate() + 1);
								let year = date.getFullYear().toString();
								if (year.length === 4 && new Date(value) < new Date().setHours(0, 0, 0, 0)) {
									if (!isUsedPastDate) {
										setIsOpenPastDate(true);
									}

									setInvoice((prev) => ({
										...prev,
										date: value,
									}));
								} else if (
									year.length === 4 &&
									new Date(value) > currentDate.setHours(0, 0, 0, 0)
								) {
									setIsOpenPastDate(false);
									newError.date = 'Date cannot be in the future';
									setError(newError);
									setInvoice((prev) => ({
										...prev,
										date: value,
									}));
								} else {
									setInvoice((prev) => ({
										...prev,
										date: value,
									}));
									setIsOpenPastDate(false);
									delete newError.date;
									setError(newError);
								}
							}}
							onChange={(value) => {
								const newError = { ...error };
								var date = new Date(value);
								var currentDate = new Date();
								currentDate.setDate(currentDate.getDate() + 1);
								let year = date.getFullYear().toString();
								if (year.length === 4 && new Date(value) < new Date().setHours(0, 0, 0, 0)) {
									setInvoice((prev) => ({
										...prev,
										date: value,
									}));
								} else if (
									year.length === 4 &&
									new Date(value) > currentDate.setHours(0, 0, 0, 0)
								) {
									setIsOpenPastDate(false);
									newError.date = 'Date cannot be in the future';
									setError(newError);
									setInvoice((prev) => ({
										...prev,
										date: value,
									}));
								} else {
									setInvoice((prev) => ({
										...prev,
										date: value,
									}));
									setIsOpenPastDate(false);
									delete newError.date;
									setError(newError);
								}
							}}
							max={getCurrentDate()}
						/>
					</div>
				</div>
				<div
					className="row"
				>
					<div className="col-xl-2 col-4">
						<InputSelect
							options={saleOrders}
							label="Sale Order #"
							placeholder="Select Sale Order"
							autoComplete="off"
							required={false}
							disabled={true}
							returnInputValue={false}
							value={invoice?.sale_order_id}
							onSelect={(option) => { }}
							error={error?.sale_order_id}
						/>
					</div>
					<div className="col-4 col-xl-3">
						<InputSelect
							options={accountList}
							dropdownType={'category-wise'}
							label="Sales Account"
							placeholder="Select Sales Account"
							autoComplete="off"
							name={'sales_account'}
							tolipIcon={{
								name: 'parent_account',
								icon: infoIcon,
								title: 'This account will be used to record sales income for this invoice.',
							}}
							required={false}
							returnInputValue={false}
							value={invoice?.account_id}
							onChange={(value) => {
								if (value === '' || value === null) {
									handleFormData('account_id', value);
								}
								setSearchAccount(value);
							}}
							onSelect={(option) => {
								handleFormData('account_id', option.key);
								if (option.value !== 'Income' && option.value !== 'Sales') {
									setIsOpenAccountWarningModal(true);
								}
							}}
							error={error?.account_id}
							onAddNew={() => setIsAddAccountModalOpen(true)}
						/>
					</div>
					<div className="col-4 col-xl-2">
						<InputField
							label="Remarks"
							placeholder="Remarks"
							type="text"
							autoComplete="off"
							required={false}
							autoFocus={isRemarkFocus}
							error={error?.remarks}
							value={invoice.remarks}
							onChange={(value) => {
								handleFormData('remarks', value, false);
							}}
						/>
					</div>
					<div className="col-6 col-xl-3">
						<InputSelect
							options={paymentMethodList}
							label="Payment Method"
							placeholder="Payment Method"
							required={true}
							autoComplete="off"
							autoFocus={isPaymentMethodFocus}
							value={invoice.payment_method}
							onChange={(value) => {
								if (value === '' || value === null) {
									handleFormData('payment_method', value);
								}
								setSearchPaymentMethod(value);
							}}
							onSelect={(options) => {
								handleFormData('payment_method', options?.key);
							}}
							error={error?.payment_method}
							onAddNew={() => {
								setIsAddPaymentMethod(true);
								setIsPaymentMethodFocus(false);
							}}
						/>
					</div>
					<div className="col-6 col-xl-2">
						<InputField
							label="Due Date"
							placeholder="Enter Due Date"
							type="date"
							autoComplete="off"
							min={invoice.date}
							value={invoice.due_date}
							onKeyPress={(e) => {
								let value = e.target.value;
								const newError = { ...error };
								var date = new Date(value);
								let year = date.getFullYear().toString();
								if (
									year.length === 4 &&
									new Date(value) < new Date(invoice.date).setHours(0, 0, 0, 0)
								) {
									newError.due_date = 'Due date cannot be in the past.';
									setError(newError);
									setInvoice((prev) => ({
										...prev,
										due_date: value,
									}));
									return;
								}
								if (year.length < 4 || year.length === 4) {
									delete newError.due_date;
									setError(newError);
									setInvoice((prev) => ({
										...prev,
										due_date: value,
									}));
								}
							}}
							onChange={(value) => {
								const newError = { ...error };
								var date = new Date(value);
								let year = date.getFullYear().toString();
								if (
									year.length === 4 &&
									new Date(value) < new Date(invoice.date).setHours(0, 0, 0, 0)
								) {
									newError.due_date = 'Due date cannot be in the past.';
									setError(newError);
									setInvoice((prev) => ({
										...prev,
										due_date: value,
									}));
									return;
								}
								if (year.length < 4 || year.length === 4) {
									delete newError.due_date;
									setError(newError);
									setInvoice((prev) => ({
										...prev,
										due_date: value,
									}));
								}
							}}
							error={error?.due_date}
							required={false}
						/>
					</div>
				</div>
				<div
					className="pb-5 pt-xl-0"
					style={{ marginBottom: '10rem' }}
				>
					<InvoiceTableHeader
						isDraft={isDraft}
						invoiceTable={invoiceTable}
						productsList={productsList}
						setInvoiceTable={setInvoiceTable}
						tableError={tableError}
						setTableError={setTableError}
					/>
				</div>
			</div>
			<InvoiceFooter
				setAdditionDiscountAndTax={setAdditionDiscountAndTax}
				additionDiscountAndTax={additionDiscountAndTax}
				totalDiscountAmount={totalDiscountAmount}
				totalTaxesAmount={totalTaxesAmount}
				totalAmount={totalAmount}
				calculateAmount={calculateAmount}
				invoiceTable={invoiceTable}
			/>

			{isOpenPastDate && (
				<Modal
					className="delivery-modal"
					title="Confirm Past Date"
					isOpen={isOpenPastDate}
					onClose={() => cancelUsedPastDate()}
					cancelButton={true}
				>
					<p>You are creating a transaction in the past date. Do you want to continue?</p>

					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start',
							marginTop: '20px',
						}}
					>
						<Button
							button="Proceed"
							type="button"
							className="btn btn-primary"
							onClick={confirmUsedPastDate}
						/>
						<span style={{ width: '20px' }}></span>
						<Button
							button="Cancel"
							type="button"
							className="btn btn-danger"
							onClick={() => cancelUsedPastDate()}
						/>
					</div>
				</Modal>
			)}
			{isAddCustomerModalOpen && (
				<AddCustomerModel
					setIsAddCustomerModalOpen={setIsAddCustomerModalOpen}
					isAddCustomerModalOpen={isAddCustomerModalOpen}
					setInvoice={setInvoice}
					setCustomerList={setCustomerList}
				/>
			)}

			{isOpenAccountWarningModal && (
				<SaleAccountAlertWarningModal
					isOpenModal={isOpenAccountWarningModal}
					setIsOpenModal={setIsOpenAccountWarningModal}
					cancelAccountSelect={() => {
						setIsOpenAccountWarningModal(false);
						setInvoice((prev) => ({
							...prev,
							account_id: null,
						}));
					}}
				/>
			)}

			{isAddDoctorModalOpen && (
				<AddDoctorModel
					setIsAddDoctorModalOpen={setIsAddDoctorModalOpen}
					isAddDoctorModalOpen={isAddDoctorModalOpen}
					setInvoice={setInvoice}
					setDoctorList={setDoctorList}
				/>
			)}
			{isAddAccountModalOpen && (
				<CreateAccountsModal
					showMessage={null}
					setSelectedId={(id) => handleFormData('account_id', id)}
					selectedId={null}
					isOpenModal={isAddAccountModalOpen}
					setIsOpenModal={setIsAddAccountModalOpen}
					getList={handleFetchAccountList}
				/>
			)}
			{isAddPaymentMethod && (
				<AddPaymentMethodModal
					type={'invoice'}
					paymentMethodList={paymentMethodList}
					isAddPaymentMethod={isAddPaymentMethod}
					setIsAddPaymentMethod={setIsAddPaymentMethod}
					setIsPaymentMethodFocus={setIsPaymentMethodFocus}
					setPurchaseBillFormData={setInvoice}
				/>
			)}
			{
				<CustomDrawer
					isOpen={isDrawerOpen}
					onClose={() => setIsDrawerOpen(false)}
					id={
						drawerType === 'customer'
							? invoice.customer_id
							: drawerType === 'doctor'
								? invoice.doctor_id
								: null
					}
					drawerType={drawerType}
					activeDrawer={'Details'}
				/>
			}
		</div>
	);
};

export default InvoiceForm;
